import React, { useRef, useEffect, useCallback } from 'react'

interface MatrixRainProps {
  burst?: boolean
}

const KATAKANA = 'アァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブヅプエェケセテネヘメレヱゲゼデベペオォコソトノホモヨョロヲゴゾドボポヴッン'
const NUMBERS = '0123456789'
const SYMBOLS = '!@#$%^&*()_+-=[]{}|;:,.<>?~`'
const ALL_CHARS = KATAKANA + NUMBERS + SYMBOLS

const MatrixRain: React.FC<MatrixRainProps> = ({ burst = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>()
  const dropsRef = useRef<number[]>([])
  const speedsRef = useRef<number[]>([])
  const burstRef = useRef(burst)

  useEffect(() => {
    burstRef.current = burst
  }, [burst])

  const getRandomChar = useCallback(() => {
    return ALL_CHARS[Math.floor(Math.random() * ALL_CHARS.length)]
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight

      const fontSize = 14
      const columns = Math.floor(canvas.width / fontSize)

      dropsRef.current = Array(columns).fill(0).map(() => Math.random() * -100)
      speedsRef.current = Array(columns).fill(0).map(() => 0.2 + Math.random() * 0.5)
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const fontSize = 14

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      ctx.font = `${fontSize}px monospace`

      const drops = dropsRef.current
      const speeds = speedsRef.current

      for (let i = 0; i < drops.length; i++) {
        const char = getRandomChar()
        const x = i * fontSize
        const y = drops[i] * fontSize

        const isBurst = burstRef.current
        const brightness = isBurst ? 1 : 0.7 + Math.random() * 0.3

        if (isBurst && Math.random() > 0.7) {
          ctx.fillStyle = `rgba(255, 255, 255, ${brightness})`
        } else if (y < fontSize * 3) {
          ctx.fillStyle = `rgba(180, 255, 180, ${brightness})`
        } else {
          const green = isBurst ? 255 : 200 + Math.floor(Math.random() * 55)
          ctx.fillStyle = `rgba(0, ${green}, 65, ${brightness})`
        }

        ctx.fillText(char, x, y)

        const speed = isBurst ? speeds[i] * 2 : speeds[i]

        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0
          speeds[i] = 0.2 + Math.random() * 0.5
        }

        drops[i] += speed
      }

      animationRef.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [getRandomChar])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  )
}

export default MatrixRain
